<template>
  <d-container fluid class="main-content-container px-4">
    <!-- Page Header -->
    <page-header
      title="Składniki"
      buttonLabel="Dodaj składnik"
      buttonLink="/ingredients/new" />

    <d-row
      style="width: 100%;"
      class="d-flex ml-0 mb-3 page-filters">
      <form
        style="width: 100%; display: flex; gap: 0.5rem; align-items: center;">
        <div style="flex: 1;">
          <d-input
            placeholder="Podaj nazwę składnika"
            v-model="filters.search" />
        </div>

        <div style="width: 300px;">
          <v-select
            :options="[
              {label: 'Składniki posiłków', value: 'available_in_food'},
              {label: 'Składniki napojów', value: 'available_in_drink'},
              {label: 'Składniki wierzchnie napojów', value: 'is_cover'},
              {label: 'Dodatki wrzucane do napojów', value: 'is_addon'},
              {label: 'Wierzchnie ozdoby do napojów', value: 'is_topping'}
            ]"
            v-model="filters.by_type"
            :searchable="false"
            :clearable="true"
            :reduce="type => type.value"
            placeholder="Wszystkie składniki">
          </v-select>
        </div>

        <d-button
          theme="success"
          outline
          class="text-nowrap"
          @click.prevent="searchWithFilters">Szukaj</d-button>

        <d-button
          theme="danger"
          outline
          class="text-nowrap"
          @click.prevent="clearFilters">Resetuj filtry</d-button>
      </form>
    </d-row>

    <d-card style="width: 100%;">
      <table class="table mt-0 mb-0 full-width">
        <thead class="thead-dark">
          <tr>
            <sortable-table-heading field="name" view="ingredients" :column="orderColumn" :direction="orderDirection">Składnik</sortable-table-heading>
            <th scope="col" class="border-0">Cechy składnika</th>
            <th scope="col" class="border-0 text-center" style="width: 1%; white-space: nowrap;">Akcje</th>
          </tr>
        </thead>
        <tbody v-if="loaded">
          <tr
            v-for="(ingredient, index) of ingredients"
            :key="'package-' + index">
            <td class="align-middle">
              <router-link :to="'/ingredients/' + ingredient.id">
                {{ ingredient.name }}
              </router-link>
            </td>
            <td
              class="align-middle"
              style="width: 250px;">
              <div v-if="ingredient.available_in_food"><small>Dostępny w posiłkach</small></div>
              <div v-if="ingredient.available_in_drink"><small>Dostępny w napojach</small></div>
              <div v-if="ingredient.available_in_drink && ingredient.is_cover"><small>Wierzchni składnik dla napojów</small></div>
              <div v-if="ingredient.available_in_drink && ingredient.is_addon"><small>Dodatek wrzucany do napojów</small></div>
              <div v-if="ingredient.available_in_drink && ingredient.is_topping"><small>Wierzchnia ozdoba dla napojów</small></div>
            </td>
            <td
              class="align-middle text-right"
              style="width: 1%; white-space: nowrap;">
              <div style="display: flex; justify-content: flex-end; gap: 0.5rem;">
                <d-button
                  theme="warning"
                  @click.prevent="showDuplicateModal(ingredient)"
                  title="Duplikuj">
                  <i class="material-icons" style="color:white">content_copy</i>
                </d-button>
                <d-button
                  theme="dark"
                  @click.native="$router.push('/ingredients/' + ingredient.id)"
                  title="Edytuj">
                  <i class="material-icons">edit</i>
                </d-button>
                <d-button
                  theme="danger"
                  :disabled="removeInProgress"
                  @click.native="removeIngredient(ingredient.id)"
                  title="Usuń">
                  <i class="material-icons">delete</i>
                </d-button>
              </div>
            </td>
          </tr>
          <tr v-if="ingredients.length === 0">
            <td
              colspan="5"
              class="text-center">
              Brak składników do wyświetlenia&hellip;
            </td>
          </tr>
        </tbody>
      </table>

      <div v-if="!loaded" class="pb-3 pt-3 text-center">
        Trwa ładowanie danych&hellip;
      </div>

      <d-alert
        v-if="loadError"
        show
        theme="warning">
        Wczytywanie danych nie powiodło się. <a href="javascript:window.location.reload();" class="alert-link">Odśwież stronę</a> aby spróbować ponownie.
      </d-alert>
    </d-card>

    <pagination :meta-data="paginationMetaData" />
  </d-container>
</template>

<script>
import debounce from 'lodash.debounce';
import HasFilters from './../mixins/HasFilters.vue';
import HasPagination from './../mixins/HasPagination.vue';
import ListUtils from './../utils/ListUtils.js';
import Pagination from './../components/common/Pagination.vue';

export default {
  name: 'ingredients',
  mixins: [
    HasFilters,
    HasPagination
  ],
  components: {
    'pagination': Pagination
  },
  data() {
    return {
      ingredients: [],
      loaded: false,
      loadError: false,
      removeInProgress: false,
      filters: {
        search: '',
        by_type: ''
      }
    };
  },
  mounted () {
    this.loadFilteredData();
  },
  methods: {
    showDuplicateModal(ingredient) {
      this.$swal({
        title: 'Potwierdzenie duplikacji',
        html: 'Czy na pewno chcesz zduplikować składnik - <b>' + (ingredient.name || '') + '</b>?',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Duplikuj',
        cancelButtonText: 'Anuluj',
        buttonsStyling: true
      }).then((result) => {
        if (result.isConfirmed) {
          this.duplicateIngredient(ingredient.id);
        }
      });
    },
    duplicateIngredient(ingredientId) {
      this.$http
        .post('/ingredients/duplicate/' + ingredientId)
        .then((response) => {
          if (response.data.success) {
            this.$swal({
              title: 'Sukces',
              text: response.data.message || 'Składnik został zduplikowany',
              icon: 'success'
            }).then(() => {
              this.loadFilteredData();
            });
          } else {
            this.$swal({
              title: 'Błąd',
              text: response.data.message || 'Nie udało się zduplikować składnika',
              icon: 'error'
            });
          }
        })
        .catch((error) => {
          this.$swal({
            title: 'Błąd',
            text: 'Wystąpił problem podczas duplikowania składnika',
            icon: 'error'
          });
          console.error(error);
        });
    },
    debouncedLoadFilteredData: debounce(function () {
      this.$bus.emit('view-filters-save');
      this.loadFilteredData();
    }, 250),
    loadFilteredData () {
      let where = {};

      if (this.filters.search) {
        where.search = this.filters.search;
      }

      if (this.filters.by_type === 'is_cover') {
        where.is_cover = 1;
      }

      if (this.filters.by_type === 'available_in_food') {
        where.available_in_food = 1;
      }

      if (this.filters.by_type === 'available_in_drink') {
        where.available_in_drink = 1;
      }

      if (this.filters.by_type === 'is_addon') {
        where.is_addon = 1;
      }

      if (this.filters.by_type === 'is_topping') {
        where.is_topping = 1;
      }

      ListUtils.loadItemsData(this, {
        endpoint: '/ingredients/items?page=' + this.currentPage,
        listField: 'ingredients',
        pagination: true,
        params: {
          where,
          order: {
            column: this.orderColumn,
            direction: this.orderDirection
          }
        }
      });
    },
    removeIngredient (ingredientID) {
      this.removeInProgress = true;

      ListUtils.removeItem(this, {
        endpointBase: '/ingredients/delete/',
        id: ingredientID,
        successTitle: 'Usunięto składnik',
        successText: 'Wybrany składnik został usunięty',
        confirmTitle: 'Czy na pewno chcesz usunąć ten składnik?',
        confirmText: 'Tej operacji nie można cofnąć',
        errorTitle: 'Wystąpił błąd',
        errorText: 'Usuwanie składnika nie powiodło się. Spróbuj ponownie.',
        successAction: () => {
          this.removeInProgress = false;
          this.loadFilteredData();
        },
        cancelAction: () => {
          this.removeInProgress = false;
        }
      });
    },
    clearFilters () {
      this.loaded = false;
      this.loadError = false;
      this.filters = {
        search: '',
        by_type: ''
      };

      this.loadFilteredData();
      this.$bus.emit('view-filters-reset');
    },
    searchWithFilters () {
      this.$bus.emit('pagination-reset');
      this.debouncedLoadFilteredData();
    }
  }
};
</script>
